import React, { useState, useCallback, useMemo } from 'react'
import { Folder, FolderOpen, ChevronRight, ChevronDown } from '@/ui/icons'
import { WaypointFolderTree } from '@/shared/api/waypointFolders'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, Button } from '@/ui/components'
import { cn } from '@/shared/utils'

interface BulkMoveWaypointsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  waypointIds: string[]
  folders: WaypointFolderTree[]
  onMove: (waypointIds: string[], folderId: string | null) => Promise<void>
  onSuccess?: () => void
}

interface FolderNodeProps {
  folder: WaypointFolderTree
  selectedFolderId: string | null
  onSelect: (folderId: string | null) => void
  depth?: number
  expandedFolders: Set<string>
  onToggleExpand: (folderId: string) => void
}

function FolderNode({
  folder,
  selectedFolderId,
  onSelect,
  depth = 0,
  expandedFolders,
  onToggleExpand,
}: FolderNodeProps) {
  const isExpanded = expandedFolders.has(folder.id)
  const isSelected = selectedFolderId === folder.id

  const handleClick = () => {
    onSelect(folder.id)
  }

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation()
    onToggleExpand(folder.id)
  }

  return (
    <div>
      <div
        onClick={handleClick}
        className={cn(
          'flex items-center gap-2 px-3 py-2 rounded-md text-sm cursor-pointer transition-colors',
          'hover:bg-gray-100',
          isSelected && 'bg-adventure-100 border border-adventure-300',
          depth > 0 && 'ml-4'
        )}
        style={{ marginLeft: `${depth * 16}px` }}
      >
        {/* Expand/collapse button */}
        {folder.children.length > 0 ? (
          <button
            onClick={handleToggle}
            className="flex-shrink-0 w-4 h-4 flex items-center justify-center text-gray-400 hover:text-gray-600"
            onMouseDown={(e) => e.stopPropagation()}
          >
            {isExpanded ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </button>
        ) : (
          <div className="w-4 h-4" />
        )}

        {/* Folder icon */}
        {isExpanded ? (
          <FolderOpen className="w-4 h-4 text-adventure-600 flex-shrink-0" />
        ) : (
          <Folder className="w-4 h-4 text-adventure-600 flex-shrink-0" />
        )}

        {/* Folder name */}
        <span className="flex-1 truncate">
          {folder.name}
          {folder.waypoint_count !== undefined && (
            <span className="ml-2 text-gray-400 text-xs">
              ({folder.waypoint_count})
            </span>
          )}
        </span>
      </div>

      {/* Children */}
      {isExpanded && folder.children.length > 0 && (
        <div className="mt-1">
          {folder.children.map((child) => (
            <FolderNode
              key={child.id}
              folder={child}
              selectedFolderId={selectedFolderId}
              onSelect={onSelect}
              depth={depth + 1}
              expandedFolders={expandedFolders}
              onToggleExpand={onToggleExpand}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export function BulkMoveWaypointsDialog({
  open,
  onOpenChange,
  waypointIds,
  folders,
  onMove,
  onSuccess,
}: BulkMoveWaypointsDialogProps) {
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null)
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set())
  const [isMoving, setIsMoving] = useState(false)

  // Reset selection when dialog opens
  React.useEffect(() => {
    if (open) {
      setSelectedFolderId(null)
      setExpandedFolders(new Set())
    }
  }, [open])

  const handleToggleExpand = useCallback((folderId: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev)
      if (next.has(folderId)) {
        next.delete(folderId)
      } else {
        next.add(folderId)
      }
      return next
    })
  }, [])

  const handleSelectRoot = useCallback(() => {
    setSelectedFolderId(null)
  }, [])

  const handleMove = useCallback(async () => {
    if (waypointIds.length === 0) return

    try {
      setIsMoving(true)
      await onMove(waypointIds, selectedFolderId)
      onOpenChange(false)
      if (onSuccess) {
        onSuccess()
      }
    } catch (error) {
      // Error handling is done in the parent component
    } finally {
      setIsMoving(false)
    }
  }, [waypointIds, selectedFolderId, onMove, onOpenChange, onSuccess])

  const selectedFolderName = useMemo(() => {
    if (selectedFolderId === null) return 'No Folder'
    
    const findFolder = (id: string, list: WaypointFolderTree[]): WaypointFolderTree | null => {
      for (const folder of list) {
        if (folder.id === id) return folder
        if (folder.children.length > 0) {
          const found = findFolder(id, folder.children)
          if (found) return found
        }
      }
      return null
    }

    const folder = findFolder(selectedFolderId, folders)
    return folder?.name || 'Unknown Folder'
  }, [selectedFolderId, folders])

  if (waypointIds.length === 0) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Move {waypointIds.length} Waypoint{waypointIds.length !== 1 ? 's' : ''} to Folder</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0 py-2">
          <div className="space-y-1">
            {/* Root option */}
            <div
              onClick={handleSelectRoot}
              className={cn(
                'flex items-center gap-2 px-3 py-2 rounded-md text-sm cursor-pointer transition-colors',
                'hover:bg-gray-100',
                selectedFolderId === null && 'bg-adventure-100 border border-adventure-300'
              )}
            >
              <Folder className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <span className="flex-1">No Folder</span>
            </div>

            {/* Folder tree */}
            {folders.map((folder) => (
              <FolderNode
                key={folder.id}
                folder={folder}
                selectedFolderId={selectedFolderId}
                onSelect={setSelectedFolderId}
                depth={0}
                expandedFolders={expandedFolders}
                onToggleExpand={handleToggleExpand}
              />
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isMoving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleMove}
            disabled={isMoving}
          >
            {isMoving ? 'Moving...' : `Move to ${selectedFolderName}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

